import React, { useState } from 'react';

import type { AiAction } from '~/components/AiChat/types';
import { useClaim } from '~/components/ClaimContainer';
import {
  ClaimReferencesProvider,
  useHasClaimReferences,
} from '~/components/Clive/ClaimReferences/ClaimReferencesProvider';
import IconButton from '~/components/core/Atomic/Buttons/IconButton';
import HistoryIcon from '~/components/icons/HistoryIcon';

import type { ActionState } from '../types';

import ActionHistoryDialog from './ActionHistoryDialog';
import { ACTION_HISTORY_TEXTS } from './texts';
import { useActionHistory } from './useActionHistory';

interface ActionHistoryProps {
  action: AiAction;
  state: ActionState;
  /** The exposure, where there is one, as the card names it. */
  exposureLabel?: string;
}

/**
 * The explanations cite the claim's documents. The hub holds one cache of them for both panes, which the
 * dialog shares; mounted where no cache is above it, the dialog brings its own.
 */
const WithClaimReferences: React.FC<{ claimId: number; children: React.ReactNode }> = ({ claimId, children }) =>
  useHasClaimReferences() ? (
    <>{children}</>
  ) : (
    <ClaimReferencesProvider claimId={claimId}>{children}</ClaimReferencesProvider>
  );

/** The open history. Mounted only while it is open, which is what keeps the read to an opening. */
const OpenActionHistory: React.FC<ActionHistoryProps & { onClose: () => void }> = ({
  action,
  state,
  exposureLabel,
  onClose,
}) => {
  const { claim } = useClaim({ claimRequired: true });
  const { versions, hasMore, isLoading, isError, loadOlder, retry } = useActionHistory(claim.id, action.action_id);

  return (
    <WithClaimReferences claimId={claim.id}>
      <ActionHistoryDialog
        action={action}
        state={state}
        exposureLabel={exposureLabel}
        versions={versions}
        hasMore={hasMore}
        isLoading={isLoading}
        isError={isError}
        onLoadOlder={loadOlder}
        onRetry={retry}
        onClose={onClose}
      />
    </WithClaimReferences>
  );
};

/**
 * The history icon on a revised action's card, in either pane, and the history it opens. The caller
 * renders it only where `hasRevisions` says the action was revised.
 */
const ActionHistory: React.FC<ActionHistoryProps> = ({ action, state, exposureLabel }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <IconButton
        size="small"
        onClick={() => setIsOpen(true)}
        title={ACTION_HISTORY_TEXTS.DIALOG_TITLE}
        aria-label={ACTION_HISTORY_TEXTS.DIALOG_TITLE}
      >
        <HistoryIcon size={20} />
      </IconButton>
      {isOpen ? (
        <OpenActionHistory
          action={action}
          state={state}
          exposureLabel={exposureLabel}
          onClose={() => setIsOpen(false)}
        />
      ) : null}
    </>
  );
};

export default ActionHistory;
